<script setup>
import { useI18n } from 'vue-i18n'
import { ref, computed } from 'vue'
import { useHead } from '@vueuse/head'

const { t, tm } = useI18n()

const openQuestionIndex = ref(null)

const toggleAnswer = index => {
  openQuestionIndex.value = openQuestionIndex.value === index ? null : index
}

const faqSchema = computed(() => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": tm('sectionThree.faq').map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
}))

useHead({
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify(faqSchema.value)
    }
  ]
})
</script>

<template>
  <div id="faq" class="questions-section-wrapper">
    <div class="questions-container">
      <h2>{{ t('sectionThree.headline') }}</h2>
      <div class="questions">
        <div
            v-for="(question, i) in tm('sectionThree.faq')"
            :key="`question_${i}`"
        >
          <div class="question">
            <div class="question-title" @click="toggleAnswer(i)">
              <img
                  src="../assets/icons/arrow.svg"
                  alt="select-down"
                  class="question-icon"
              />
              {{ question.question }}
            </div>
            <div
                class="answer"
                v-show="openQuestionIndex === i"
                v-html="question.answer"
            >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.questions-section-wrapper {
  position: relative;
  background: url("../assets/img/background-mocca.png") no-repeat center center;
  background-size: cover;
}

.questions-section-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url("../assets/img/background-pattern.png") no-repeat center center;
  background-size: cover;
  opacity: 0.01;
  pointer-events: none;
  z-index: 1;
}

.questions-container {
  max-width: 1080px;
  display: flex;
  flex-direction: column;
  justify-items: flex-start;
  margin: 0 auto;
  padding: 20px 30px;
  z-index: 2;
  gap:32px;
}

.questions-container h2 {
  font-size: 34px;
  line-height: 43px;
  color: #242e30;
  text-align: center;
  margin-top: 50px;
  margin-bottom: 30px;
}

.question {
  margin-bottom: 20px;
}

.question-title {
  font-family: 'JETSansVF2', Arial, sans-serif;
  font-size: 22px;
  font-weight: 700;
  line-height: 26px;
  cursor: pointer;
  color: #242e30;
  padding: 10px;
  display: inline-flex;
  align-items: flex-start;
}

.question-title .question-icon {
  width: 11px;
  height: 21px;
  margin-right: 10px;
  margin-top: 2px;
}

.answer {
  font-family: 'JETSansVF2', Arial, sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  padding: 5px 20px 10px 45px;
  color: #242E30;
  transition: max-height 0.3s ease-out;
}

.answer {
  overflow: hidden;
}

.answer.open {
  max-height: 300px;
}

@media (max-width: 768px) {
  .questions-section-wrapper {
    min-height: 100vh;
  }

  .questions-container {
    gap: 16px;
  }

  .questions-container h2 {
    font-size: 20px;
    font-weight: 900;
    line-height: 24px;
    text-align: center;
  }

  .question-title {
    font-size: 16px;
    line-height: 19px;
    white-space: normal;
  }

  .answer {
    font-size: 14px;
    line-height: 17px;
  }

  .question-title .question-icon {
    width: 15px;
    height: 11px;
    margin-top: 5px;
  }
}
</style>
